import { ExportType } from '@va/constants';
import { ExportFormat, useRequestExport } from '@va/dashboard/api-client/export';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { getWebsiteTimezone, getWebsiteUrl } from '@va/dashboard/selectors/core';
import { usePrepareLatestVisitorsListRequests } from '@va/dashboard/shared/visitors';
import { useExportNotification } from '@va/dashboard/util-hooks';
import { useLocale } from '@va/localization';
import { getAccountOwnerEmail } from '@va/standalone/shared/selectors';
import { isWixApp, isWixMiniApp } from '@va/util/helpers';
import moment from 'moment';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export const useExportRbData = () => {
  const { locale } = useLocale();
  const { execute } = useRequestExport();
  const websiteId = useSelector(getInstanceId);
  const websiteUrl = useSelector(getWebsiteUrl);
  const timezone = useSelector(getWebsiteTimezone);
  const userEmail = useSelector(getAccountOwnerEmail);

  const { from, payload, until } = usePrepareLatestVisitorsListRequests();
  const { showErrorNotification, showExportStartedNotification } = useExportNotification();

  const startExport = useCallback(
    async (format: ExportFormat) => {
      try {
        await execute(websiteId, {
          from: moment(from).unix(),
          until: moment(until).unix(),
          locale: locale,
          exportType: ExportType.rawSessions,
          websiteUrl: websiteUrl,
          payload: payload,
          format: format,
          timezone,
          ...(!isWixApp() && !isWixMiniApp() && { emailTo: userEmail }),
        });

        showExportStartedNotification();
      } catch {
        showErrorNotification();
      }
    },
    [
      execute,
      from,
      locale,
      payload,
      showErrorNotification,
      showExportStartedNotification,
      timezone,
      until,
      userEmail,
      websiteId,
      websiteUrl,
    ],
  );

  return {
    downloadCSV: () => startExport(ExportFormat.csv),
    downloadXLSX: () => startExport(ExportFormat.xlsx),
  };
};
